import { useMemo, useRef, useState } from 'react'
import dayjs from 'dayjs'
import { ColDef, ICellRendererParams, IRowNode, IsRowSelectable, SelectionChangedEvent } from 'ag-grid-community'
import { AgGridReact } from 'ag-grid-react'
import { HStack } from '@musinsa/mwms-ds'
import { PrimaryButton, SecondaryButton, Typography } from '@musinsa/layer-components-desktop'
import GridTableV2 from '@/components/tableV2/GridTableV2'
import { ExportButton, Pagination } from '@/components/tableV2/GridTableV2Toolbar'
import FileInput from '@/components/content/FileInput'
import { usePagination } from '@/hooks/usePagination'
import Badge from '@/components/content/Badge'
import { DATE_FORMAT } from '@/utils/consts'
import { Utils } from '@/utils/Utils'
import { GRID_SIZE } from '@/utils/grid/grid'
import { ConfirmModal } from '@/components/content/ConfirmModal'
import Button from '@/components/content/Button'
import MAlert from '@/utils/modal/MAlert'
import MToast from '@/utils/modal/MToast'
import { CategoryPriceListRes, PriceStatusCode } from '@/services/settlement/settlementPricing/types'
import { useDeleteCategoryPrices, useEndCategoryPrices } from '@/services/settlement/settlementPricing/useSettlementPricingQueries'
import { resolvePriceStatus } from '../../consts'
import { CategoryPriceFormSchemaType } from '../../schemas/categoryPriceFormSchema'
import { CategoryPriceFormModal } from './CategoryPriceFormModal'
import { PricingExcelUploadModal } from './PricingExcelUploadModal'
import { PricingTableStyle } from '../pricingGridStyle'

interface Props {
  rowData: CategoryPriceListRes[]
  pageInfo: PageInfoType
  onChangePage: (page: number) => void
  onChangePageSize: (size: number) => void
  onDownload: () => void
  onDownloadTemplate: () => void
  refetch: () => void
  isDownloading?: boolean
  isDownloadDisabled?: boolean
  isLoading?: boolean
}

const price = (v?: number | null) => (v || v === 0 ? `${Utils.getCommaNum(v)}원` : '-')
const date = (v?: string | null) => (v ? dayjs(v).format(DATE_FORMAT.date) : '-')
// 진행중=초록 / 대기=주황 / 종료=회색
const STATUS: Record<PriceStatusCode, { text: string; bg: string; fg: string }> = {
  ACTIVE: { text: '진행중', bg: '#E7F6EC', fg: '#1FA34A' },
  PENDING: { text: '대기', bg: '#FFF3E0', fg: '#E08A00' },
  ENDED: { text: '종료', bg: '#F0F0F0', fg: '#8A8A8A' },
}

const status = (r: CategoryPriceListRes) => resolvePriceStatus(r.startDate, r.endDate)
// bizest 대>중>소 브레드크럼 (BE에서 category_depth_1/2/3 매핑, depth 없으면 -)
const breadcrumb = (r: CategoryPriceListRes) =>
  [r.bizestLarge?.name, r.bizestMiddle?.name, r.bizestSmall?.name].filter(Boolean).join(' > ') || '-'

interface ManageCellProps extends ICellRendererParams<CategoryPriceListRes> {
  onEdit: (r: CategoryPriceListRes) => void
  onDeleteRow: (r: CategoryPriceListRes) => void
  onEndRow: (r: CategoryPriceListRes) => void
}

// 관리(PRD §5-3-1): 대기 → [수정][삭제] / 진행중 → [종료] / 종료 → -
function ManageCellRenderer({ data, onEdit, onDeleteRow, onEndRow }: ManageCellProps) {
  if (!data) return null
  const s = status(data)
  return (
    <HStack gap={4} align="center">
      <SecondaryButton variant="outlined" size="small" label="수정" disabled={s !== 'PENDING'} onClick={() => onEdit(data)} />
      <SecondaryButton variant="outlined" size="small" label="삭제" disabled={s !== 'PENDING'} onClick={() => onDeleteRow(data)} />
      <SecondaryButton variant="outlined" size="small" label="종료" disabled={s !== 'ACTIVE'} onClick={() => onEndRow(data)} />
    </HStack>
  )
}

export function CategoryPriceTable({
  rowData,
  pageInfo,
  onChangePage,
  onChangePageSize,
  onDownload,
  onDownloadTemplate,
  refetch,
  isDownloading,
  isDownloadDisabled,
  isLoading,
}: Props) {
  const gridRef = useRef<AgGridReact>(null)
  const retryInputRef = useRef<HTMLInputElement>(null)
  const [editing, setEditing] = useState<(CategoryPriceFormSchemaType & { id: number }) | null>(null)
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [confirm, setConfirm] = useState<{ kind: 'END' | 'DELETE'; ids: number[] } | null>(null)
  const [bulkOpen, setBulkOpen] = useState(false)
  const [selectedRows, setSelectedRows] = useState<CategoryPriceListRes[]>([])
  const endMutation = useEndCategoryPrices()

  // LOGISPRD-9667 — 시작일 당일에는 종료 불가(종료일=어제가 시작일보다 앞서는 역전 방지). 기획 확정: 시작 다음 날부터 종료 가능.
  const requestEnd = (rows: CategoryPriceListRes[]) => {
    const today = dayjs().format(DATE_FORMAT.date)
    if (rows.some((r) => r.startDate >= today)) {
      MAlert.show('적용 시작일 당일에는 종료할 수 없습니다.\n적용 시작 다음 날부터 종료 처리해 주세요.')
      return
    }
    setConfirm({ kind: 'END', ids: rows.map((r) => r.id) })
  }
  const deleteMutation = useDeleteCategoryPrices()

  const size = pageInfo.size || GRID_SIZE.SETTLEMENT
  const page = pageInfo.page || 0
  const totalCount = pageInfo.totalCount || 0
  const selectedCount = selectedRows.length
  // PRD §5-2: 삭제=선택 전부 '대기'일 때만 / 종료=선택 전부 '진행중'일 때만 (0건·혼합·종료포함 시 비활성)
  const canDelete = selectedCount > 0 && selectedRows.every((r) => status(r) === 'PENDING')
  const canEnd = selectedCount > 0 && selectedRows.every((r) => status(r) === 'ACTIVE')

  const paginationProps = usePagination({ totalCount, currentPage: page, pageSize: size, onChangePage, onPageSizeChange: onChangePageSize })

  const openCreate = () => {
    setEditing(null)
    setIsFormOpen(true)
  }
  const openEdit = (r: CategoryPriceListRes) => {
    setEditing({
      id: r.id,
      country: r.country.code,
      serviceType: r.serviceType.code,
      orderType: r.orderType.code,
      logisticsCategoryCode: r.logisticsCategory.code,
      unitType: r.unitType.code,
      unitPrice: String(r.unitPrice),
      startDate: r.startDate,
      endDate: r.endDate,
    })
    setIsFormOpen(true)
  }

  const runConfirm = async () => {
    if (!confirm) return
    const { kind, ids } = confirm
    try {
      if (kind === 'END') {
        await endMutation.mutateAsync({ ids })
        MToast.show('선택한 단가가 종료되었습니다')
      } else {
        await deleteMutation.mutateAsync({ ids })
        MToast.show('선택한 단가가 삭제되었습니다')
      }
      gridRef.current?.api?.deselectAll()
      await refetch()
      setConfirm(null)
    } catch (e) {
      MAlert.show(Utils.getApiErrMessage(e, kind === 'END' ? '단가 종료에 실패했습니다.' : '단가 삭제에 실패했습니다.'))
    }
  }

  const isRowSelectable: IsRowSelectable<CategoryPriceListRes> = (node: IRowNode<CategoryPriceListRes>) =>
    !!node.data && status(node.data) !== 'ENDED'

  const handleSelectionChanged = (e: SelectionChangedEvent<CategoryPriceListRes>) => {
    setSelectedRows(e.api.getSelectedRows())
  }

  const columnDefs = useMemo<ColDef<CategoryPriceListRes>[]>(
    () => [
      { colId: 'country', headerName: '국가', width: 80, valueGetter: ({ data }) => data?.country?.name ?? '-' },
      { colId: 'serviceType', headerName: '서비스 항목', width: 130, valueGetter: ({ data }) => data?.serviceType?.name ?? '-' },
      { colId: 'unitType', headerName: '단위', width: 70, valueGetter: ({ data }) => data?.unitType?.name ?? '-' },
      { colId: 'orderType', headerName: '주문유형', width: 90, valueGetter: ({ data }) => data?.orderType?.name ?? '-' },
      // 물류카테고리는 코드(logistics_category_code)로 통일 표기 — 필터·등록 모달과 동일 (UI-BE 싱크 협의)
      { colId: 'logisticsCategory', headerName: '물류카테고리', width: 120, valueGetter: ({ data }) => data?.logisticsCategory?.code ?? '-' },
      {
        colId: 'bizestCategory',
        headerName: 'bizest 카테고리',
        width: 180,
        valueGetter: ({ data }) => (data ? breadcrumb(data) : '-'),
        cellClass: 'ellipsis-cell',
      },
      { field: 'unitPrice', headerName: '단가', width: 100, valueFormatter: ({ value }) => price(value) },
      {
        colId: 'status',
        headerName: '상태',
        width: 88,
        cellStyle: { display: 'flex', alignItems: 'center' },
        // eslint-disable-next-line react/no-unstable-nested-components
        cellRenderer: ({ data }: ICellRendererParams<CategoryPriceListRes>) => {
          if (!data) return null
          const s = STATUS[status(data)]
          return <Badge type="CUSTOM" text={s.text} backgroundColor={s.bg} textColor={s.fg} />
        },
      },
      { field: 'startDate', headerName: '시작일', width: 104, valueFormatter: ({ value }) => date(value) },
      { field: 'endDate', headerName: '종료일', width: 104, valueFormatter: ({ value }) => date(value) },
      { field: 'createdBy', headerName: '생성자', width: 140, valueFormatter: ({ value }) => value || '-' },
      { field: 'createdAt', headerName: '생성일', width: 110, valueFormatter: ({ value }) => date(value) },
      { field: 'updatedBy', headerName: '수정자', width: 140, valueFormatter: ({ value }) => value || '-' },
      { field: 'updatedAt', headerName: '수정일', width: 110, valueFormatter: ({ value }) => date(value) },
      {
        colId: 'manage',
        headerName: '관리',
        width: 180,
        sortable: false,
        cellStyle: { display: 'flex', alignItems: 'center' },
        cellRenderer: ManageCellRenderer,
        cellRendererParams: {
          onEdit: openEdit,
          onDeleteRow: (r: CategoryPriceListRes) => setConfirm({ kind: 'DELETE', ids: [r.id] }),
          onEndRow: (r: CategoryPriceListRes) => requestEnd([r]),
        },
      },
      // 남는 폭 채움(빈 스페이서)
      { headerName: '', flex: 1, minWidth: 0, sortable: false },
    ],
    [],
  )

  const handleRetryFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.currentTarget.files?.[0]
    if (f) setUploadFile(f)
    e.currentTarget.value = ''
  }

  const bulkMenuItem: React.CSSProperties = { padding: '8px 12px', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }

  return (
    <>
      <PricingTableStyle />
      <input ref={retryInputRef} type="file" accept=".xlsx, .xls" style={{ display: 'none' }} onChange={handleRetryFile} />
      <GridTableV2<CategoryPriceListRes>
        gridKey="BL04_CATE"
        ref={gridRef}
        idKey="id"
        className="pricing-grid"
        rowData={rowData}
        isLoading={isLoading}
        gridOptions={{ rowHeight: 52 }}
        columnDefs={columnDefs}
        totalCount={totalCount}
        rowStartIndex={page * size}
        rowSelectable
        useColumnNo
        showTableHeader={false}
        isRowSelectable={isRowSelectable}
        onSelectionChanged={handleSelectionChanged}
        noRowsOverlayComponentParams={{ mainContent: '조회 결과가 없습니다.' }}
        title={
          <>
            <HStack gap={12} align="center">
              <HStack gap={4} align="center">
                <Typography name="base-text-m" colorToken="fg">
                  전체
                </Typography>
                <Typography name="base-text-m-semibold" colorToken="fg">
                  {totalCount}
                </Typography>
              </HStack>
              <Typography name="base-text-m" colorToken="fg">
                {`${selectedCount}건 선택`}
              </Typography>
              <SecondaryButton
                variant="outlined"
                size="small"
                label="삭제"
                disabled={!canDelete}
                onClick={() => setConfirm({ kind: 'DELETE', ids: selectedRows.map((r) => r.id) })}
              />
              <SecondaryButton
                variant="outlined"
                size="small"
                label="종료"
                disabled={!canEnd}
                onClick={() => requestEnd(selectedRows)}
              />
            </HStack>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end' }}>
              <ExportButton label="전체 다운로드" onClick={onDownload} disabled={isDownloading || isDownloadDisabled} />
              <div style={{ position: 'relative' }}>
                <SecondaryButton variant="outlined" size="small" label="단가 일괄 등록 ▾" onClick={() => setBulkOpen((o) => !o)} />
                {bulkOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 4px)',
                      right: 0,
                      zIndex: 20,
                      background: '#fff',
                      border: '1px solid #e6e6e6',
                      borderRadius: 4,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                      minWidth: 168,
                    }}
                  >
                    <FileInput
                      accept=".xlsx, .xls"
                      trigger={<div style={bulkMenuItem}>엑셀 업로드</div>}
                      onChange={(file) => {
                        setUploadFile(file)
                        setBulkOpen(false)
                      }}
                    />
                    <div
                      role="button"
                      tabIndex={0}
                      style={{ ...bulkMenuItem, borderTop: '1px solid #f0f0f0' }}
                      onClick={() => {
                        onDownloadTemplate()
                        setBulkOpen(false)
                      }}
                    >
                      엑셀 템플릿 다운로드
                    </div>
                  </div>
                )}
              </div>
              <PrimaryButton variant="filled" size="small" label="단가 등록" onClick={openCreate} />
              <Pagination {...paginationProps} />
            </div>
          </>
        }
      />

      <CategoryPriceFormModal
        open={isFormOpen}
        key={editing?.id ?? 'create'}
        defaultValues={editing}
        onClose={() => setIsFormOpen(false)}
        refetch={refetch}
      />

      {uploadFile && (
        <PricingExcelUploadModal
          initialFile={uploadFile}
          onClose={() => setUploadFile(null)}
          onRetry={() => {
            setUploadFile(null)
            retryInputRef.current?.click()
          }}
          onDownloadTemplate={onDownloadTemplate}
          refetch={refetch}
        />
      )}

      {confirm && (
        <ConfirmModal
          title={
            confirm.kind === 'END'
              ? `해당 ${confirm.ids.length}건의 단가를 종료하시겠습니까?`
              : `해당 ${confirm.ids.length}건의 카테고리 단가를 삭제하시겠습니까?`
          }
          description={
            confirm.kind === 'END'
              ? `종료일이 어제(${dayjs().subtract(1, 'day').format(DATE_FORMAT.date)})로 설정됩니다.`
              : '대기 상태만 삭제됩니다. (진행중/종료는 제외)'
          }
          footer={
            <HStack gap={8}>
              <Button kind="secondary" content="취소" onClick={() => setConfirm(null)} />
              <Button kind="primary_light" content={confirm.kind === 'END' ? '종료' : '삭제'} onClick={runConfirm} />
            </HStack>
          }
        />
      )}
    </>
  )
}
